import { env } from "@/config";
import FacebookIcon from "./assets/FacebookIcon";
import LinkedInIcon from "./assets/LinkedInIcon";
import InstagramIcon from "./assets/InstagramIcon";
import NetworxText from "./assets/NetworxText";
import { isMobile } from "@/utils/is-mobile";
import { prependCdnResourcesUrl } from "@/utils/network";

export default function Footer() {
  const isMobileView = isMobile();

  return (
    <footer className="w-full overflow-hidden bg-[#06141b] px-4 sm:px-6 pt-16 text-white md:px-12 lg:pt-24 font-light">
      <div className="mx-auto max-w-page">
        <div className="flex flex-col gap-12 lg:flex-row lg:justify-between">
          <div className="flex flex-col items-start gap-8">
            <a
              href="/"
              className="flex size-14 sm:size-16 items-center justify-center rounded-2xl bg-secondary"
              aria-label="Networx home"
            >
              <img
                src={prependCdnResourcesUrl("/images/global/networx-logo-symbol-v2.svg")}
                alt=""
                className="size-5 sm:size-6 brightness-0 invert"
              />
            </a>

            <p className="max-w-xs text-3xl font-semibold leading-none tracking-tight">
              Get free quotes
              <br />
              from local pros
            </p>

            <div className="mt-2 flex items-center gap-14">
              {SOCIAL_MEDIA_LINKS.map(({ url, icon, label }) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center transition-opacity hover:opacity-80"
                  aria-label={label}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          <div className="flex gap-12 sm:gap-32">
            <FooterLinkColumn title="Company" links={COMPANY_LINKS} />
            <FooterLinkColumn title="For Homeowners" links={HOMEOWNER_LINKS} />
          </div>
        </div>

        <div className="mt-12 sm:mt-24 flex flex-col gap-8 text-sm text-white/70 lg:flex-row lg:items-center lg:justify-between">
          {!isMobileView && <FooterCopyright />}

          <nav className="flex flex-wrap gap-x-6 sm:gap-x-10 gap-y-3 sm:gap-y-4">
            {BOTTOM_LINKS.map((item) =>
              "id" in item ? (
                <button
                  key={item.label}
                  type="button"
                  id={item.id}
                  className="text-sm transition-colors !text-background/70"
                >
                  {item.label}
                </button>
              ) : (
                <a
                  key={item.url}
                  href={item.url}
                  rel="nofollow"
                  className="text-sm transition-colors !text-background/70"
                >
                  {item.label}
                </a>
              )
            )}
          </nav>
        </div>

        {isMobileView && <FooterCopyright className="mt-10" />}

        <div className="mt-12 sm:mt-24">
          <NetworxText className="w-full" />
        </div>
      </div>
    </footer>
  );
}

function FooterLinkColumn({ title, links }: FooterLinkColumnProps) {
  return (
    <nav className="flex flex-col gap-4">
      <p className="text-sm text-background/70">{title}</p>
      {links.map((item) => (
        <a
          key={item.url}
          href={item.url}
          className="text-lg !text-background transition-colors"
        >
          {item.label}
        </a>
      ))}
    </nav>
  );
}

const FooterCopyright = ({ className }: { className?: string }) => {
  return (
    <p className={className}>
      © {new Date().getFullYear()} Networx Systems, Inc. All Rights
      Reserved
    </p>
  );
}

interface FooterLinkColumnProps {
  title: string;
  links: FooterLink[];
}

interface FooterLink {
  url: string;
  label: string;
}

type BottomLink =
  | { url: string; label: string }
  | { label: string; id: "accessibility_tools_link" };

const COMPANY_LINKS = [
  {
    url: "/about",
    label: "About",
  },
  {
    url: "/careers",
    label: "Careers",
  },
  {
    url: globalThis.AFFILIATES_URL,
    label: "Affiliate Program",
  },
  {
    url: "/contact",
    label: "Contact",
  },
];

const HOMEOWNER_LINKS = [
  {
    url: "/review/company",
    label: "Write a Review",
  },
  {
    url: "/cost-guide",
    label: "Cost Guide",
  },
  {
    url: "/experiences",
    label: "Experiences",
  },
  {
    url: "/articles",
    label: "Blog",
  },
];

const BOTTOM_LINKS: BottomLink[] = [
  {
    url: "/terms-use",
    label: "Terms & Conditions",
  },
  {
    url: "/privacy",
    label: "Privacy Policy",
  },
  {
    id: "accessibility_tools_link",
    label: "Accessibility Tools",
  },
  {
    url: "/do-not-sell",
    label: "Do Not Sell My Personal Info",
  },
];

const SOCIAL_MEDIA_LINKS = [
  {
    url: "https://www.instagram.com/networxteam/",
    icon: <InstagramIcon className="h-5 w-5 text-white" />,
    label: "Instagram",
  },
  {
    url: "https://www.facebook.com/networx.pros",
    icon: <FacebookIcon className="h-5 w-5 text-white" />,
    label: "Facebook",
  },
  {
    url: "https://www.linkedin.com/company/networx-systems",
    icon: <LinkedInIcon className="h-5 w-5 text-white" />,
    label: "LinkedIn",
  },
];
